<template>
  <div v-if="showVerificationMessage" class="flex flex-col items-center gap-4 text-center text-sm text-foreground">
    <p class="m-0">
      {{ tr('Please verify your email address.') }}
      <br>
      {{ tr('We have sent a verification email to :email.', { ':email': email }) }}
      <br>
      {{ tr('Click the link in the email to activate your account.') }}
    </p>
    <p class="m-0 text-muted-foreground">
      {{ tr('If you don\'t see the email, please check your spam folder.') }}
    </p>
  </div>

  <form
    v-else
    class="flex flex-col gap-4"
    novalidate
    @submit.prevent="() => submit()"
  >
    <div class="flex gap-4 max-sm:flex-col">
      <div v-for="field in nameFields" :key="field.key" class="flex flex-1 flex-col gap-1.5">
        <ui-label :for="'signup-' + field.key">{{ field.label }}<span class="text-destructive">*</span></ui-label>
        <ui-input
          :id="'signup-' + field.key"
          v-model="$data[field.key]"
          :autocomplete="field.autocomplete"
          :disabled="isInvite"
        />
      </div>
    </div>

    <div v-if="!isInvite" class="flex flex-col gap-1.5">
      <ui-label for="signup-company">{{ tr('Organization name') }}</ui-label>
      <ui-input id="signup-company" v-model="companyName" autocomplete="organization" />
    </div>

    <div class="flex flex-col gap-1.5">
      <ui-label for="signup-email">{{ tr('Email') }}<span class="text-destructive">*</span></ui-label>
      <ui-input
        id="signup-email"
        v-model="email"
        type="email"
        autocomplete="email"
        :disabled="isInvite"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Phone number') }}<span class="text-destructive">*</span></ui-label>
      <phone-field ref="telInput" v-model="phone" />
    </div>

    <div class="flex flex-col gap-1.5">
      <ui-label for="signup-password">{{ tr('Password') }}<span class="text-destructive">*</span></ui-label>
      <password-field
        id="signup-password"
        v-model="password"
        autocomplete="new-password"
        :placeholder="tr('Enter at least 8 characters...')"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <ui-label for="signup-password-again">{{ tr('Password again') }}<span class="text-destructive">*</span></ui-label>
      <password-field
        id="signup-password-again"
        v-model="passwordAgain"
        autocomplete="new-password"
        :placeholder="tr('Repeat your password')"
      />
    </div>

    <div class="mt-1 flex flex-col gap-3">
      <label class="flex cursor-pointer items-start gap-2.5 text-sm text-foreground [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-4">
        <ui-checkbox v-model="gtcCheckbox" class="mt-0.5" />
        <span><span v-html="gtcCheckboxLabel" /> <span class="text-destructive">*</span></span>
      </label>
      <label class="flex cursor-pointer items-start gap-2.5 text-sm text-foreground [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-4">
        <ui-checkbox v-model="privacyCheckbox" class="mt-0.5" />
        <span><span v-html="privacyCheckboxLabel" /> <span class="text-destructive">*</span></span>
      </label>
      <label class="flex cursor-pointer items-start gap-2.5 text-sm text-foreground [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-4">
        <ui-checkbox v-model="newsletterCheckbox" class="mt-0.5" />
        <span>{{ tr('I would like to be notified by email about news and the methods to make ads even more effective') }}</span>
      </label>
    </div>

    <ui-button
      type="submit"
      size="lg"
      class="mt-2 w-full"
      :disabled="isLoading"
    >
      <loader-circle-icon v-if="isLoading" class="animate-spin" />
      {{ tr('Signup') }}
    </ui-button>

    <router-link to="/auth/login" class="text-sm font-semibold text-foreground underline decoration-muted-foreground/50 underline-offset-4 hover:text-primary">
      {{ tr('If you already have an account, log in here') }}
    </router-link>
  </form>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import AuthService from '@/services/Auth.js';
import PasswordField from '@/ui/components/PasswordField.vue';
import PhoneField from '@/ui/components/PhoneField.vue';
import { getUtmSource } from '@/utils/storage.js';
import { isValidEmail } from '@/utils/validators.js';

export default {
  name: 'SignupView',
  components: {
    LoaderCircleIcon: LoaderCircle,
    PasswordField,
    PhoneField,
  },
  data: () => ({
    isLoading: false,
    showVerificationMessage: false,
    firstname: '',
    lastname: '',
    companyName: '',
    timezone: 'UTC',
    email: '',
    phone: '',
    password: '',
    passwordAgain: '',
    gtcCheckbox: false,
    privacyCheckbox: false,
    newsletterCheckbox: false,
  }),
  computed: {
    timezones() {
      return this.$store.getters['app/timezones'];
    },
    token() {
      return this.$route.params.token;
    },
    isInvite() {
      return !!this.token;
    },
    gtcCheckboxLabel() {
      return this.tr('I accept the <a href="https://infinite.ad/en/unspecified-contract-terms/" target="_blank">Terms and Conditions</a>');
    },
    privacyCheckboxLabel() {
      return this.tr('I accept the <a href="https://infinite.ad/en/data-management-rules/" target="_blank">Privacy Policy</a>');
    },
    // Hungarian names start with the last name.
    nameFields() {
      const fields = [
        { key: 'firstname', label: this.tr('First name'), autocomplete: 'given-name' },
        { key: 'lastname', label: this.tr('Last name'), autocomplete: 'family-name' },
      ];

      return this.$root.language === 'hu' ? fields.reverse() : fields;
    },
  },
  watch: {
    timezones: {
      immediate: true,
      handler(val) {
        if (val) {
          this.setSystemTimezone();
        }
      },
    },
    showVerificationMessage(val) {
      if (val) {
        this.$emit('override-title', this.tr('Check Your Email'));
        this.$emit('center-title');
      }
    },
  },
  created() {
    this.loadRecaptchaScript();

    if (this.isInvite) {
      AuthService.invitationValidate(this.token)
        .then(invitation => {
          this.email = invitation.email;
          this.firstname = invitation.firstname;
          this.lastname = invitation.lastname;

          return invitation;
        })
        .catch(err => {
          this.error(err);
          this.$router.replace('/auth/login');
        })
        .finally(() => {
          this.$store.commit('app/isLoading', false);
        });
    }
    else {
      this.$store.commit('app/isLoading', false);
    }
  },

  beforeUnmount() {
    this.removeRecaptchaScripts();
  },
  methods: {
    resetForm() {
      this.firstname = '';
      this.lastname = '';
      this.companyName = '';
      this.timezone = 'UTC';
      this.email = '';
      this.phone = '';
      this.password = '';
      this.passwordAgain = '';
      this.gtcCheckbox = false;
      this.privacyCheckbox = false;
      this.newsletterCheckbox = false;

      this.setSystemTimezone();
    },
    setSystemTimezone() {
      try {
        const userTimezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
        for (const timezone of this.timezones) {
          if (timezone.name === userTimezone) {
            this.timezone = userTimezone;
            break;
          }
        }
      }
      catch (_) {
        // ignore.
      }
    },
    submit() {
      if (!this.validate()) {
        return;
      }

      // Load and execute reCAPTCHA
      this.isLoading = true;
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(process.env.VUE_APP_RECAPTCHA_SITE_KEY, { action: 'signup' })
          .then(token => this.submitForm(token))
          .catch(err => this.error(err));
      });
    },

    submitForm(recaptchaToken) {
      this.isLoading = true;
      this.$store
        .dispatch('auth/signup', {
          hash: this.token,
          email: this.email,
          contactPhone: this.phone,
          password: this.password,
          firstname: this.firstname,
          lastname: this.lastname,
          companyName: this.companyName,
          newsletter: this.newsletterCheckbox,
          timezone: this.timezone,
          recaptchaToken,
          source: getUtmSource(),
        })
        .then(resp => {
          if (window.fbq) {
            window.fbq('trackCustom', 'TrialStarted');
          }
          if (window.ttq) {
            window.ttq.track('TrialStarted');
            window.ttq.track('CompleteRegistration');
          }
          if (window.rdt) {
            window.rdt('track', 'Custom', { customEventName: 'TrialStarted' });
          }
          if (window.lintrk) {
            // TrialStarted conversion.
            window.lintrk('track', { conversion_id: 21984649 });
          }
          if (window.gtag) {
            window.gtag('event', 'conversion', { 'send_to': 'AW-11565729405/LqAvCM6K6YwaEP2M_Ior' });
          }

          if (this.isInvite) {
            setTimeout(() => {
              this.$router.replace({ path: '/' });
              this.resetForm();
            }, 250);
          }
          else {
            this.showVerificationMessage = true;
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    validate() {
      this.$toast.clear();

      if (!this.isInvite) {
        if (this.$root.language == 'en') {
          if (!this.validateFirstName()) {
            return false;
          }
          if (!this.validateLastName()) {
            return false;
          }
        }
        else if (this.$root.language == 'hu') {
          if (!this.validateLastName()) {
            return false;
          }
          if (!this.validateFirstName()) {
            return false;
          }
        }
        if (this.email.trim().length === 0) {
          this.$toast.error(this.tr('E-mail field cannot be empty!'));
          return false;
        }
        if (!isValidEmail(this.email)) {
          this.$toast.error(this.tr('E-mail provided has an incorrect format!'));
          return false;
        }
        if (this.phone.trim().length === 0) {
          this.$toast.error(this.tr('Phone number field cannot be empty!'));
          return false;
        }
        if (!this.$refs.telInput.isValid()) {
          this.$toast.error(this.tr('Phone number provided has an incorrect format!'));
          return false;
        }
      }

      if (this.password.length < 8) {
        this.$toast.error(this.tr('The password must be at least 8 characters long.'));
        return false;
      }
      if (this.password !== this.passwordAgain) {
        this.$toast.error(this.tr('Passwords given do not match!'));
        return false;
      }

      if (!this.gtcCheckbox) {
        this.$toast.error(this.tr('To use :app, you must accept the Terms and Conditions.'));
        return false;
      }
      if (!this.privacyCheckbox) {
        this.$toast.error(this.tr('To use :app, you must accept the Privacy Policy.'));
        return false;
      }

      return true;
    },
    validateFirstName() {
      if (this.firstname.trim().length === 0) {
        this.$toast.error(this.tr('First name field cannot be empty!'));
        return false;
      }
      return true;
    },
    validateLastName() {
      if (this.lastname.trim().length === 0) {
        this.$toast.error(this.tr('Last name field cannot be empty!'));
        return false;
      }
      return true;
    },
    getCapthcaToken() {
      return new Promise((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(process.env.VUE_APP_RECAPTCHA_SITE_KEY, { action: 'signup' })
            .then(token => resolve(token))
            .catch(err => reject(err));
        });
      });
    },
    loadRecaptchaScript() {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.VUE_APP_RECAPTCHA_SITE_KEY}`;
      document.head.appendChild(script);
    },
    removeRecaptchaScripts() {
      // Remove script tags
      const scripts = document.querySelectorAll('script[src*="recaptcha"]');
      scripts.forEach(script => script.remove());

      // Remove any reCAPTCHA iframes
      const iframes = document.querySelectorAll('iframe[src*="recaptcha"]');
      iframes.forEach(iframe => iframe.remove());

      // Remove any remaining reCAPTCHA related elements
      const recaptchaElements = document.querySelectorAll('.grecaptcha-badge, .g-recaptcha');
      recaptchaElements.forEach(element => element.remove());

      // Clear any global reCAPTCHA variables
      if (window.grecaptcha) {
        try {
          delete window.grecaptcha;
        }
        catch(e) {
          window.grecaptcha = undefined;
        }
      }
    },
  },
};
</script>
